import React, { useCallback } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import {
  MultiDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { withLocalePrefix } from 'shared/utils/translation';
import { useQueryClient } from '@tanstack/react-query';
import { ItemWithOptions } from 'talent_search/shared/types';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { InlineMarkdown } from '@grnhse/seedling/lib/birch/typography';
import styled from 'styled-components';

const t = withLocalePrefix('talent_matching.rediscovery.filter_row.quality');

export type RejectionReasonsDropdownType = 'include' | 'exclude';

type Props = {
  fetchRejectionReasons: (term: string) => Promise<ItemWithOptions[]>;
  type: RejectionReasonsDropdownType;
  helperText?: string;
  tooltipText?: string;
};

const formFieldByType: Record<RejectionReasonsDropdownType, string> = {
  include: 'include_rejection_reasons',
  exclude: 'exclude_rejection_reasons',
};

const Row = styled(Stack)`
  div {
    flex-grow: 1;
  }
`;

const RejectionReasonsDropdown = ({
  fetchRejectionReasons,
  type,
  helperText,
  tooltipText,
}: Props) => {
  const queryClient = useQueryClient();
  const { watch, setValue } = useFormContext();
  const formField = formFieldByType[type];
  const selectedItems: Item[] = watch(formField) ?? [];

  const toNestedDropdown = (items: ItemWithOptions[]): Item[] => {
    return items.map((item) => ({
      label: item.label,
      value: item.value,
      disabled: true,
      items: item.options,
    }));
  };

  const fetchItems = useCallback(
    async (term: string) => {
      const queryKey = ['rejectionReasons', term];

      const cachedData: ItemWithOptions[] | null | undefined =
        queryClient.getQueryData(queryKey);

      if (cachedData) {
        return { items: toNestedDropdown(cachedData) };
      }

      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: () => fetchRejectionReasons(term),
      });

      return { items: toNestedDropdown(items) };
    },
    [fetchRejectionReasons, queryClient]
  );

  const onSelectedItemsChange = (e: { selectedItems: Array<Item> }) => {
    setValue(formField, e.selectedItems, { shouldDirty: true });
  };

  const dropdownProps = useMultiDropdown({
    fetchItems,
    onSelectedItemsChange,
    selectedItems,
    preventLoadOnMount: true,
  });

  return (
    <Row alignItems="center" spacing={0.5} direction="row">
      <MultiDropdown dropdownProps={dropdownProps}>
        <MultiDropdown.Field
          data-provides={`talent-rediscovery-${type}-rejection-reasons-dropdown`}
          label={t(`${type}_rejection_reasons`)}
          helperText={helperText}
        />
        <MultiDropdown.Flyout />
      </MultiDropdown>
      {tooltipText && (
        <HelpTooltip
          label={(<InlineMarkdown text={tooltipText} />) as unknown as string}
          ariaLabel={tooltipText}
        />
      )}
    </Row>
  );
};

export default RejectionReasonsDropdown;
